import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Mail } from 'lucide-react';
import { useT } from '../../i18n';
import {
  ACCOUNT_SIGN_IN_NO_API,
  ACCOUNT_SIGN_IN_UNREACHABLE,
  requestAccountCode,
  verifyAccountCode,
  type AccountSignInSession,
  type JoinedClub,
} from '../../lib/api/accountSignIn';
import { formatOtpCodeInput } from './otpCode';
import { useRetryWait } from '../ui/useRetryWait';

// ─────────────────────────────────────────────────────────────────────
// THE CUSTOMER SIGN-IN FLOW: an address, then a code (#751).
//
// Extracted from `PlayerSignInScreen` so the CLUB PORTAL's entrance can be the
// same one, rather than a second implementation of the same two steps against
// a different endpoint.
//
// ── WHICH DOOR THIS IS, AND WHY IT MATTERS ────────────────────────
//
// `/auth/otp` — the GLOBAL account door. It provisions an account for a
// previously unknown address, which is the whole point: a new player signing
// in for the first time has no identity yet.
//
// `/auth/club/otp` is the other one, and using it here would be the defect the
// #837 review caught: it issues a challenge ONLY for an existing active local
// identity, so an unknown address gets the non-enumerating 202 and no code —
// a dead step rather than a sign-in. One customer entrance, one endpoint.
//
// ── IT KNOWS NO MORE THAN THE SERVER SAYS ─────────────────────────
//
// `request` answers the same 202 for an address that is unknown, known, or
// belongs to a disabled account. So this moves to the code step either way and
// the copy promises only that a code was sent — saying «we found you» would be
// an address oracle built on the client after the server refused to be one.
//
// ── A THROTTLE SAYS HOW LONG, AND MEANS IT (#778 Q7) ──────────────
//
// «Prøv igjen om litt» was all a throttled person was told, while the wait
// behind it could be most of an hour. The screen now shows the server's own
// `Retry-After`, counts it down, and holds the button that would be refused
// until it has passed. Per action: «send» spends the mail budget and «sign in»
// the wrong-code budget, so a refused resend never disables a code already
// in hand (`useRetryWait`).
//
// ── THE SESSION IS HANDED UP, NOT INSTALLED ───────────────────────
//
// `onSignedIn` receives it. Which holder owns an account session, and which
// surface the app renders afterwards, is the gate's decision — one place owns
// what «signed in» means, and a screen that also set it would be a second.
// ─────────────────────────────────────────────────────────────────────

/** Mirrors `OTP_LIFETIME_MS`. For the sentence, not for any decision. */
const CODE_LIFETIME_MINUTES = 10;

export interface AccountCodeSignInProps {
  /** Called with the session the door issued, once a code is accepted. */
  onSignedIn: (session: AccountSignInSession, email: string) => void;
  /**
   * Ask for the thirty-day session when the code is spent (#1091). The player
   * screen decides it — its checkbox, or the native app — and the club portal
   * never passes it, so the portal keeps twelve hours.
   */
  remember?: boolean;
  /**
   * The level of the step's heading (#1144). The club portal puts its own
   * `<h1>` above this, so the step is an `<h2>` there; the player screen has
   * no other heading, so its step heading is the page's `<h1>`. Same classes
   * either way: the level is structure (1.3.1), not size.
   */
  headingLevel?: 1 | 2;
}

type Step =
  | { name: 'email'; error: string | null }
  | { name: 'sending' }
  | { name: 'code'; error: string | null; resent: boolean }
  | { name: 'verifying' }
  /**
   * SIGNED IN, AND MADE A MEMBER (owner decision 3A, 2026-10-31). The code
   * accepted a club's invitation to this address, so the person is told —
   * once, here, before the session is handed on — rather than finding a club
   * they never said yes to. The session is already issued; «Fortsett» only
   * hands it up.
   */
  | { name: 'joined'; session: AccountSignInSession; email: string; clubs: JoinedClub[] };

export function AccountCodeSignIn({
  onSignedIn,
  remember = false,
  headingLevel = 2,
}: AccountCodeSignInProps) {
  const t = useT();
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  // One id for both steps: only one form is mounted at a time, so the message
  // always belongs to the field that is on screen.
  const errorId = useId();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>({ name: 'email', error: null });
  const wait = useRetryWait<'send' | 'verify'>();

  const busy = step.name === 'sending' || step.name === 'verifying';
  const onCodeStep = step.name === 'code' || step.name === 'verifying';

  // ── FOCUS GOES WHERE THE NEXT STEP IS (#1144, WCAG 2.4.3) ─────────
  // After «Send engangskode» the e-mail form is replaced and focus fell to
  // <body>, so a keyboard or screen-reader user had to find the code field.
  // Keyed on the step's NAME, so an error shown on the code step (the name
  // stays «code») does not steal focus again, while a failed check (back from
  // «verifying») does return it to the field to try again.
  const codeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step.name === 'code') codeRef.current?.focus();
  }, [step.name]);
  // A refusal's own sentence wins over a wait still counting from an earlier
  // one; a throttle sets no sentence of its own, so its countdown shows.
  const error = (step.name === 'email' || step.name === 'code' ? step.error : null) ?? wait.notice;

  const messageFor = (code: string): string =>
    code === 'not_configured'
      ? t('playerSignIn.unavailable')
      : code === ACCOUNT_SIGN_IN_NO_API
        ? t('playerSignIn.unavailable')
        : code === ACCOUNT_SIGN_IN_UNREACHABLE
          ? t('playerSignIn.network')
          : t('playerSignIn.network');

  const ask = async (resend = false) => {
    if (busy || wait.waiting('send')) return;
    wait.release('send');
    setStep({ name: 'sending' });
    const outcome = await requestAccountCode(email.trim());
    if (outcome.code === null) {
      setStep({ name: 'code', error: null, resent: resend });
      return;
    }
    if (outcome.code === 'too_many_attempts') {
      wait.start('send', outcome.retryAfterSeconds ?? 0);
    }
    // A FAILED RESEND STAYS ON THE CODE STEP. They may still have the first
    // code, and throwing them back to the address field would lose it.
    const message = outcome.code === 'too_many_attempts' ? null : messageFor(outcome.code);
    setStep(
      resend ? { name: 'code', error: message, resent: false } : { name: 'email', error: message },
    );
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || wait.waiting('verify')) return;
    wait.release('verify');
    setStep({ name: 'verifying' });
    const outcome = await verifyAccountCode(email.trim(), code.trim(), remember);
    if (outcome.code === null && outcome.session) {
      const clubs = outcome.session.joined ?? [];
      if (clubs.length > 0) {
        setStep({ name: 'joined', session: outcome.session, email: email.trim(), clubs });
        return;
      }
      onSignedIn(outcome.session, email.trim());
      return;
    }
    // ONE MESSAGE FOR EVERY REFUSAL the server collapses: wrong code, expired,
    // none requested, disabled account. Distinguishing them here would undo the
    // server's silence from the client side. A spent budget is the exception
    // the server itself makes — a 429 with a wait — and it gets that wait.
    if (outcome.code === 'too_many_attempts') {
      wait.start('verify', outcome.retryAfterSeconds ?? 0);
    }
    setStep({
      name: 'code',
      error:
        outcome.code === 'too_many_attempts'
          ? null
          : outcome.code === 'invalid_code'
            ? t('playerSignIn.refused')
            : outcome.code === 'account_closed'
              ? // Named only after the code proved the mailbox (#1062): the
                // server says nothing about a closure before that.
                t('errors.account_closed')
              : messageFor(outcome.code ?? ''),
      resent: false,
    });
  };

  return (
    // NO CARD OF ITS OWN. The surface is the caller's — the player screen and
    // the club portal draw different chrome around the same two steps — and a
    // card nested in a card is what rendering one here would produce.
    <>
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-ink-3" aria-hidden="true" />
        <Heading className="font-display text-lg font-semibold">
          {step.name === 'joined'
            ? t('playerSignIn.joinedTitle', { club: step.clubs[0]!.name })
            : onCodeStep
              ? t('playerSignIn.sentTitle')
              : t('playerSignIn.title')}
        </Heading>
      </div>

      {step.name === 'joined' && (
        <div role="status" data-testid="signin-joined">
          {step.clubs.slice(1).map((club) => (
            <p key={club.slug} className="font-display text-lg font-semibold">
              {t('playerSignIn.joinedTitle', { club: club.name })}
            </p>
          ))}
          <p className="text-xs text-ink-3 mb-4">{t('playerSignIn.joinedLead')}</p>
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => onSignedIn(step.session, step.email)}
          >
            {t('playerSignIn.joinedContinue')}
          </button>
        </div>
      )}

      {!onCodeStep && step.name !== 'joined' && (
        <>
          <p className="text-xs text-ink-3 mb-4">{t('playerSignIn.lead')}</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void ask();
            }}
            className="space-y-3"
          >
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                {t('playerSignIn.emailLabel')}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // ANOTHER ADDRESS IS ANOTHER BUDGET: the mail budget is per
                  // address, so a wait for the old one does not hold this one.
                  wait.release('send');
                }}
                autoComplete="email"
                inputMode="email"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="input w-full"
                disabled={busy}
              />
            </label>
            {error && (
              <p id={errorId} role="alert" className="text-xs text-danger">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={busy || email.trim() === '' || wait.waiting('send')}
            >
              {busy ? t('playerSignIn.sending') : t('playerSignIn.send')}
            </button>
          </form>
        </>
      )}

      {onCodeStep && (
        <>
          <p className="text-xs text-ink-3 mb-4">
            {t('playerSignIn.sentLead', {
              email: email.trim(),
              minutes: CODE_LIFETIME_MINUTES,
            })}
          </p>
          <form onSubmit={verify} className="space-y-3">
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                {t('playerSignIn.codeLabel')}
              </span>
              <input
                ref={codeRef}
                type="text"
                value={code}
                // NORMALISED, NOT TRUNCATED (see `otpCode.ts`). `maxLength`
                // is gone on purpose: it cut a pasted «123 456» to six
                // characters before this handler ever ran, and the cap now
                // lives after the non-digits are stripped.
                onChange={(e) => setCode(formatOtpCodeInput(e.target.value))}
                autoComplete="one-time-code"
                inputMode="numeric"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="input w-full text-center tracking-[0.3em] text-lg"
                disabled={busy}
              />
            </label>
            {error && (
              <p id={errorId} role="alert" className="text-xs text-danger">
                {error}
              </p>
            )}
            {step.name === 'code' && step.resent && !error && (
              <p className="text-xs text-ink-3">{t('playerSignIn.resent')}</p>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={busy || code.trim() === '' || wait.waiting('verify')}
            >
              {busy ? t('playerSignIn.verifying') : t('playerSignIn.verify')}
            </button>
            <button
              type="button"
              className="btn-ghost w-full text-xs"
              onClick={() => void ask(true)}
              disabled={busy || wait.waiting('send')}
            >
              {t('playerSignIn.resend')}
            </button>
            <button
              type="button"
              className="btn-ghost w-full text-xs"
              onClick={() => {
                setCode('');
                // A different address starts with its own budgets.
                wait.clear();
                setStep({ name: 'email', error: null });
              }}
              disabled={busy}
            >
              {t('playerSignIn.wrongAddress')}
            </button>
          </form>
        </>
      )}
    </>
  );
}
